// Mock Planet API client
// All functions simulate network calls with setTimeout and return Chinese-labeled mock data

export type PlanetID = string;

export interface PlanetBrief {
  planetId: PlanetID;
  nickname: string;
  levelName: string;
  levelIndex: number;
  energyMain: number;     // 主能量值
  energyUsable: number;   // 可用能量
  estimatedUSDT: number;  // 预估价值（USDT）
  hasPlanet: boolean;
}

export interface EnergyUnit {
  id: string;
  amount: number;            // 单位能量
  frozen: number;            // 冻结部分
  stealCount: number;        // 被偷次数
  createdAt: string;         // 生成时间
  stealWindowOpenAt: string; // 可被偷开始时间
  freezeExpireAt: string;    // 冻结到期
}

export interface RankingItem {
  planetId: PlanetID;
  nickname: string;
  avatar?: string;
  energyMain: number;
  rank: number;
}

const delay = (ms = 500) => new Promise((res) => setTimeout(res, ms));

export const planetApi = {
  async me(): Promise<PlanetBrief> {
    await delay(400);
    return {
      planetId: '10001',
      nickname: '行星-华尔街',
      levelName: 'Bronze-3',
      levelIndex: 2,
      energyMain: 52400,
      energyUsable: 12680,
      estimatedUSDT: +( (52400 + 12680) / 40 ).toFixed(2),
      hasPlanet: false,
    };
  },

  async detail(planetId: PlanetID): Promise<PlanetBrief> {
    await delay(300);
    return {
      planetId,
      nickname: `行星-${planetId}`,
      levelName: 'Silver-1',
      levelIndex: 5,
      energyMain: 88888,
      energyUsable: 22222,
      estimatedUSDT: +( (88888 + 22222) / 40 ).toFixed(2),
      hasPlanet: false,
    };
  },

  async mint(input: { lng: number; lat: number; name?: string }): Promise<{ planetId: PlanetID; nickname: string }>{
    await delay(800);
    return { planetId: String(Math.floor(Math.random() * 1e6)), nickname: input.name || '我的新星球' };
  },

  async mintPreview(lng: number, lat: number) {
    await delay(200);
    return {
      previewImage: '/assets/planet/preview.png',
      lng, lat,
      suggestName: `星球-${lng.toFixed(2)}-${lat.toFixed(2)}`
    };
  },

  async energies(planetId: PlanetID, page = 1): Promise<{ list: EnergyUnit[]; page: number; total: number }>{
    await delay(350);
    const now = Date.now();
    const list: EnergyUnit[] = Array.from({ length: 10 }).map((_, i) => ({
      id: `${planetId}-${page}-${i}`,
      amount: 50 + Math.round(Math.random() * 100),
      frozen: 10 + Math.round(Math.random() * 30),
      stealCount: Math.round(Math.random() * 4),
      createdAt: new Date(now - (i + page) * 3600_000).toISOString(),
      stealWindowOpenAt: new Date(now - (i + page - 0.5) * 3600_000).toISOString(),
      freezeExpireAt: new Date(now + (24 - i) * 3600_000).toISOString(),
    }));
    return { list, page, total: 50 };
  },

  async claimEnergy(id: string): Promise<{ ok: boolean; deltaMain: number; deltaUsable: number }>{
    await delay(300);
    return { ok: true, deltaMain: 12, deltaUsable: 8 };
  },

  async stealEnergy(fromPlanetId: PlanetID): Promise<{ ok: boolean; got: number }>{
    await delay(500);
    return { ok: true, got: 7 };
  },

  async destroy(planetId: PlanetID): Promise<{ ok: boolean }>{
    await delay(600);
    return { ok: true };
  },

  async exchangeUsdt(planetId: PlanetID, amountEnergy: number): Promise<{ ok: boolean; usdt: number }>{
    await delay(600);
    return { ok: true, usdt: +(amountEnergy / 40).toFixed(2) };
  },

  async rankings(type: 'energy' | 'steal', period: 'day' | 'week' | 'all'): Promise<RankingItem[]>{
    await delay(400);
    return Array.from({ length: 20 }).map((_, i) => ({
      planetId: String(2000 + i),
      nickname: `${type === 'energy' ? '能量' : '偷取'}达人-${i + 1}`,
      energyMain: 10_000 + i * 777,
      rank: i + 1,
    }));
  },

  async dividends(period: 'day' | 'week' | 'month'): Promise<{ period: string; amountUSDT: number }>{
    await delay(250);
    return { period, amountUSDT: +(Math.random() * 100).toFixed(2) };
  },
};

export default planetApi;

